import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { BiLoaderAlt } from "react-icons/bi";
import { useMutation } from "react-query";
import styled, { css } from "styled-components/macro";
import { RadioProceed } from "../../../component/component.style";
import { NumberField } from "../../../component/formFields";
import { rotate } from "../../../component/QuestionForm";
import { useRecommendation } from "../../../contexts/recommendation-context";
import { validatePincode } from "../../../utils/enquiry";
import { createDateString } from "../../../utils/helpers";
import { yup } from "../../../utils/yup";
import { FieldErrorMessage, Name } from "../Result";

const paybimaSchema = yup.object({
  education: yup.string().required("Ediucation is required"),
  pincode: yup
    .string()
    .required("Pincode is required")
    .min(6, "Please enter a valid pincode")
    .test(
      "validatePincode",
      "Please enter a valid pincode",
      async givenPincode => {
        if (givenPincode.length === 6) {
          const { status } = await validatePincode(givenPincode);

          return status;
        }
        return true;
      },
    ),
  mobile_no: yup
    .string()
    .required("Mobile no is required")
    .min(10, "Please enter a valid phone number"),
  dob: yup
    .string()
    .required("Dob is required")
    .minYears({ minYears: 25, errorMessage: "Minimum age should be 25" })
    .maxYears({ maxYears: 65, errorMessage: "Age should be less than 65" }),
});

function PayBimaForm({ sumAssured, policyTerm }) {
  const { answers } = useRecommendation();

  const fullName = answers["name"];

  let [firstName, lastName] = ["", ""];

  if (fullName) {
    [firstName, lastName] = fullName.split(" ");
  }

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: { tobacco_consumer: "no" },
    resolver: yupResolver(paybimaSchema),
  });

  const onSubmit = data => {
    const quote_data = {
      gender_age: [
        [
          answers["identify"] === "male" ? "M" : "F",
          answers["age"],
          createDateString(data.dob),
        ],
      ],
      term_policy_term: policyTerm,
      term_tobacco_habit: data.tobacco_consumer === "yes" ? "Y" : "N",
      term_sum_assured: String(parseInt(sumAssured)),
      term_pincode: data.pincode,
      term_pay_option: "R",
      term_pay_frequency: "0",
      term_pay_lumpsum: "0",
      limited_pay_term: "5",
      term_paytype_option: "N",
      term_lead_first_name: firstName,
      term_lead_last_name: lastName,
      term_lead_mobile: data.mobile_no,
      term_lead_email: answers["mail"],
      first_name: firstName,
      last_name: lastName,
      mobile_no: data.mobile_no,
      email_id: answers["mail"],
      term_insured_age: answers["age"],
      annual_income: answers["income"],
      education: data.education,
      occupation: answers["work"],
    };

    const sendData = {
      ...data,
      dob: createDateString(data.dob),
      first_name: firstName,
      last_name: lastName,
      email: answers["mail"],
      section: "term",
      quote_data: JSON.stringify(quote_data),
    };

    if (sumAssured) sendData.sum_assured = sumAssured;

    const form = document.createElement("form");
    form.setAttribute(
      "action",
      "https://uat.paybima.com/term/recommend_quotes",
    );
    form.setAttribute("method", "POST");
    Object.keys(sendData).forEach(key => {
      const inputData = document.createElement("input");

      inputData.setAttribute("value", sendData[key]);
      inputData.setAttribute("name", key);
      inputData.setAttribute("id", key);

      form.appendChild(inputData);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);

    form.addEventListener("submit", console.log);
  };

  const { mutate, isLoading: isPincodeValidationLoading } = useMutation(
    validatePincode,
    {
      onSuccess: ({ status }) => {
        if (!status) {
          setError("pincode", {
            type: "manual",
            message: "Please enter a valid pincode",
          });
          return;
        }
        clearErrors("pincode");
      },
    },
  );

  const handlePincodeChange = evt => {
    clearErrors("pincode");
    const { value: givenPincode } = evt.target;
    if (givenPincode.length === 6) {
      mutate(givenPincode);
    }
  };

  return (
    <form
      className="d-flex flex-column justify-content-center align-items-center"
      css={`
        margin: 50px auto 50px;
      `}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete={"off"}
      noValidate
    >
      <h4 className="mb-5">
        Hey <Name>{firstName}</Name>, just a few more questions to get your
        quotes!{" "}
      </h4>
      <QuestionsWrapper>
        <div className="question d-flex justify-content-between align-items-center mb-3 w-100">
          <span className="question_title w-50">Do you consume tobbaco?</span>
          <div className="input_area d-flex justify-content-evenly w-50 radio-wrapper">
            <div className="w-50">
              <input
                type="radio"
                id="yes_tobbaco"
                name="tobbaco_consumer"
                value="yes"
                {...register("tobacco_consumer")}
              />
              <label htmlFor="yes_tobbaco">Yes</label>
            </div>
            <div className="w-50">
              <input
                type="radio"
                id="no_tobbaco"
                value="no"
                {...register("tobacco_consumer")}
              />
              <label htmlFor="no_tobbaco">No</label>
            </div>
          </div>
        </div>

        <div className="question d-flex justify-content-between align-items-center mb-3">
          <span className="question_title w-50">
            Give us your education details
          </span>
          <div className="input_area w-50">
            <select
              id="education_streams"
              className="w-100"
              {...register("education")}
            >
              {/* <option value="" disabled selected>
                Select Education
              </option> */}
              <option value="graduate_and_above">Graduate and Above</option>
              <option value="12th_pass">12th Pass</option>
              <option value="10th_pass">10th Pass</option>
            </select>
            {errors.education && (
              <FieldErrorMessage>{errors.education.message}</FieldErrorMessage>
            )}
          </div>
        </div>

        <div className="question d-flex justify-content-between align-items-center mb-3">
          <span className="question_title w-50">Tell us your pincode</span>
          <div className="input_area w-50">
            <div
              css={`
                position: relative;
              `}
            >
              <StyledNumberField
                className="w-100"
                maxLength={6}
                {...register("pincode")}
                onChange={evt => {
                  handlePincodeChange(evt);
                  register("pincode").onChange(evt);
                }}
              />
              {isPincodeValidationLoading && (
                <div
                  css={`
                    position: absolute;
                    left: 100%;
                    top: 50%;
                    transform: translate(30%, -50%);
                  `}
                >
                  <BiLoaderAlt
                    css={`
                      font-size: 1.2em;
                      animation: ${rotate} 0.37s linear infinite;
                    `}
                  />
                </div>
              )}
            </div>
            {errors.pincode && (
              <FieldErrorMessage>{errors.pincode.message}</FieldErrorMessage>
            )}
          </div>
        </div>

        <div className="question d-flex justify-content-between align-items-center mb-3">
          <span className="question_title w-50">Mobile number</span>
          <div className="input_area d-flex flex-column justify-content-evenly align-items-center w-50">
            <input
              type="tel"
              className="w-100"
              maxLength={10}
              {...register("mobile_no")}
            />

            <small>Note: This will be your user Id for login!</small>
            {errors.mobile_no && (
              <FieldErrorMessage>{errors.mobile_no.message}</FieldErrorMessage>
            )}
          </div>
        </div>

        <div className="question d-flex justify-content-between align-items-center mb-3">
          <span className="question_title w-50">Date of Birth</span>
          <div className="input_area d-flex flex-column justify-content-evenly align-items-center w-50">
            <input
              className="w-100"
              type="date"
              {...register("dob")}
              min="1955-01-01"
              max="2010-12-31"
            />
            {errors.dob && (
              <FieldErrorMessage>{errors.dob.message}</FieldErrorMessage>
            )}
          </div>
        </div>
      </QuestionsWrapper>

      <RadioProceed type="submit" className="mt-5">
        Proceed
      </RadioProceed>
    </form>
  );
}

export { PayBimaForm };

const QuestionsWrapper = styled.div`
  width: 80%;
  input,
  input:focus,
  select {
    outline: none;
    border: 1px solid rgb(1, 130, 255);
    padding: 10px;
    color: rgb(1, 130, 255);
  }
  & .radio-wrapper {
    & input[type="radio"] {
      display: none;
    }
    label {
      border: 1px solid rgb(1, 130, 255);
      padding: 10px 0px;
      width: 100%;
      text-align: center;
      color: rgb(1, 130, 255);
    }
    input[type="radio"]:checked + label {
      background: rgb(1, 130, 255);
      color: white;
    }
  }
`;

const StyledNumberField = styled(NumberField)`
  border-radius: 0;
  margin: 0;
  font-size: 1rem;
`;

export const inputStyle = css`
  outline: none;
  border: 1px solid rgb(1, 130, 255);
  padding: 10px;
  color: rgb(1, 130, 255);
`;
